import { Box, Stack, Typography, styled } from '@mui/material';
import { InfoCircle, SearchCircle } from '~/assets/icons';
import { theme } from '~/shared/styles/theme';

const SponsorDashboardPreSearchState = () => (
  <PreSearchCenteredWrapper>
    <PreSearchStack spacing={4}>
      <PreSearchIconWrapper>
        <SearchCircle width={72} height={72} />
      </PreSearchIconWrapper>
      <Stack spacing={1.5} alignItems="center">
        <PreSearchTitle>Search for Patient Cohorts</PreSearchTitle>
        <PreSearchSubtitle>
          Use the filters on the left to search for patients matching your trial
          criteria.
        </PreSearchSubtitle>
        <PreSearchSubtitle>
          Results will show aggregated statistics to protect patient privacy.
        </PreSearchSubtitle>
      </Stack>
      <PreSearchInfoCard>
        <PreSearchInfoHeader>
          <PreSearchInfoIcon>
            <InfoCircle width={20} height={20} />
          </PreSearchInfoIcon>
          <PreSearchInfoTitle>How results are displayed</PreSearchInfoTitle>
        </PreSearchInfoHeader>
        <Stack spacing={1.25}>
          <PreSearchBulletRow>
            <PreSearchBulletDot>•</PreSearchBulletDot>
            <Typography
              variant="body2"
              color={theme.palette.custom.mediumBrown}
            >
              <PreSearchBoldText>Count ranges</PreSearchBoldText> (e.g.,{' '}
              {'"51–100"'}) are shown instead of exact numbers to protect
              privacy.
            </Typography>
          </PreSearchBulletRow>
          <PreSearchBulletRow>
            <PreSearchBulletDot>•</PreSearchBulletDot>
            <Typography
              variant="body2"
              color={theme.palette.custom.mediumBrown}
            >
              <PreSearchBoldText>Percentage ranges</PreSearchBoldText> (e.g.,{' '}
              {'"20%–40%"'}) support relative comparisons between categories.
            </Typography>
          </PreSearchBulletRow>
          <PreSearchBulletRow>
            <PreSearchBulletDot>•</PreSearchBulletDot>
            <Typography
              variant="body2"
              color={theme.palette.custom.mediumBrown}
            >
              <PreSearchBoldText>Suppressed cells</PreSearchBoldText> are shown
              as {'"–"'} when fewer than 11 patients are in a category.
            </Typography>
          </PreSearchBulletRow>
          <PreSearchBulletRow>
            <PreSearchBulletDot>•</PreSearchBulletDot>
            <Typography
              variant="body2"
              color={theme.palette.custom.mediumBrown}
            >
              <PreSearchBoldText>Minimum cohort</PreSearchBoldText> of 11
              patients is required to view any results.
            </Typography>
          </PreSearchBulletRow>
        </Stack>
      </PreSearchInfoCard>
    </PreSearchStack>
  </PreSearchCenteredWrapper>
);

export default SponsorDashboardPreSearchState;

const PreSearchCenteredWrapper = styled(Stack)(() => ({
  flex: 1,
  minHeight: `calc(100vh - ${theme.tokens.layout.headerHeight}px - ${theme.spacing(40)})`,
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
}));

const PreSearchStack = styled(Stack)(() => ({
  alignItems: 'center',
  textAlign: 'center',
  gap: theme.spacing(4),
  maxWidth: 720,
  margin: '0 auto',
}));

const PreSearchIconWrapper = styled(Stack)(() => ({
  width: 120,
  height: 120,
  borderRadius: '50%',
  backgroundColor: theme.palette.custom.cohortIconBg,
  color: theme.palette.custom.grayGreen,
  alignItems: 'center',
  justifyContent: 'center',
}));

const PreSearchTitle = styled(Typography)(() => ({
  fontSize: 26,
  fontWeight: 700,
  color: theme.palette.custom.darkBrown,
}));

const PreSearchSubtitle = styled(Typography)(() => ({
  fontSize: 16,
  color: theme.palette.custom.mediumBrown,
}));

const PreSearchInfoCard = styled(Box)(() => ({
  width: '100%',
  backgroundColor: theme.palette.common.white,
  borderRadius: 12,
  border: `1px solid ${theme.palette.custom.cohortCardBorder}`,
  padding: theme.spacing(3.5, 4),
  textAlign: 'left',
}));

const PreSearchInfoHeader = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2.5),
}));

const PreSearchInfoIcon = styled(Stack)(() => ({
  color: theme.palette.custom.cohortMetaText,
  alignItems: 'center',
}));

const PreSearchInfoTitle = styled(Typography)(() => ({
  fontSize: 14,
  fontWeight: 700,
  color: theme.palette.custom.darkBrown,
}));

const PreSearchBulletRow = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
}));

const PreSearchBulletDot = styled('span')(() => ({
  fontSize: 16,
  lineHeight: 1.6,
  color: theme.palette.custom.darkBrown,
  marginTop: theme.spacing(0.5),
}));

const PreSearchBoldText = styled('span')({
  fontWeight: 700,
});
